import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api, ApiError, fmtDate, fmtDateTime, fmtDay, toDayString } from '../../lib/api'
import type { AppRow, HomeSummary } from '../../lib/types'
import { statusMeta } from '../../lib/status'
import { Button, Card, PanelTitle } from '../../ds'
import { Dot, EmptyHint, ErrorText, Num, PageSpinner, StatusChip } from '../../components/ui'
import { effectiveZone } from '../../lib/tz'
import { buildWeek, CHIP_TONES, groupActions, type TodoItem } from './week'
import { formatLabel } from '../database/forms'
import { clearLegacyNextActionPatch } from '../database/edit'

/** Panels are edge-to-edge frames; the corner marks must not be clipped. */
const PANEL: React.CSSProperties = { padding: 0 }

/**
 * Today dashboard backed by /api/v1/home/summary (server-side full-data
 * aggregates). Counts are never extrapolated from a 200-row page; upcoming
 * interviews are cross-application and sorted by actual scheduled time; the
 * todo badge and the checklist are the SAME data source — summary.todo_items
 * (standalone actions + legacy derived next_actions), so the rendered list can
 * never disagree with the sidebar count. Error states show a retry entry
 * rather than a misleading "nothing here".
 */
export function TodayPage() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const [toast, setToast] = useState('')
  // 正在提交的那一条待办的 id（完成 / 延期共用一个，两者不会同时发起）。
  const [busyTodoId, setBusyTodoId] = useState<number | null>(null)

  const summaryQ = useQuery({
    queryKey: ['home', 'summary', { limit: 5 }],
    queryFn: () => api.get<HomeSummary>('/api/v1/home/summary?limit=5'),
  })

  // 抽屉 invalidateAfterAction 刷新 actions / calendar / home / notifications；
  // 首页以前只失效 summary，日历上的待办和岗位详情卡片会停在旧状态。
  const invalidateAfterTodo = () => {
    qc.invalidateQueries({ queryKey: ['home'] })
    qc.invalidateQueries({ queryKey: ['actions'] })
    qc.invalidateQueries({ queryKey: ['calendar'] })
    qc.invalidateQueries({ queryKey: ['notifications'] })
    // 完成 / 延期会重写 applications.next_action(_due_at)，数据库页的「下一步 /
    // 截止」两列读的正是它：不失效 apps 的话，在 10s staleTime 内切到数据库页
    // 看到的还是刚刚那条已经完成的待办。
    qc.invalidateQueries({ queryKey: ['apps'] })
  }

  const doneMut = useMutation({
    mutationFn: async ({ actionId, applicationId }: { actionId: number | null; applicationId: number | null }) => {
      if (actionId != null) return api.post(`/api/v1/actions/${actionId}/done`, { done: true })
      if (applicationId == null) {
        throw new ApiError('derived_todo', '该待办没有关联岗位，无法完成', 409)
      }
      const fresh = await api.get<AppRow>(`/api/v1/applications/${applicationId}`)
      return api.patch(`/api/v1/applications/${applicationId}`, clearLegacyNextActionPatch(fresh.version))
    },
    onSuccess: () => {
      setToast('')
      invalidateAfterTodo()
    },
    onError: (e: unknown) => setToast(e instanceof ApiError ? e.message : '操作失败'),
    onSettled: () => setBusyTodoId(null),
  })

  const postponeMut = useMutation({
    mutationFn: (actionId: number) => api.post(`/api/v1/actions/${actionId}/postpone`, { days: 1 }),
    onSuccess: () => {
      setToast('')
      invalidateAfterTodo()
    },
    onError: (e: unknown) => setToast(e instanceof ApiError ? e.message : '延期失败'),
    onSettled: () => setBusyTodoId(null),
  })

  // 失败文案必须自己会消失。以前它只被 setToast 写入、从不清除：完成待办失败
  // 一次，那条红字就永远挂在首页顶上——哪怕后面几次都成功了，也没有关闭按钮。
  useEffect(() => {
    if (!toast) return
    const t = window.setTimeout(() => setToast(''), 6000)
    return () => window.clearTimeout(t)
  }, [toast])

  const summary = summaryQ.data

  // The checklist renders the same unified rows the badge counts. Server rows
  // with an action_id are standalone actions (complete/postpone live on
  // /actions/:id); rows without one are legacy derived from the application's
  // next_action — completing them clears that mirror via PATCH /applications/:id.
  const groups = useMemo(() => {
    const items: TodoItem[] = (summary?.todo_items ?? []).map((r) => ({
      id: r.id,
      application_id: r.application_id,
      action_id: r.action_id,
      title: r.title,
      due_ts: r.due_ts,
      due_date: r.due_day,
      done_at: null,
      remind_me: false,
      // 首页摘要没有提醒时间：这里只是渲染用的行，从不回写 PATCH。
      remind_at: null,
      priority: 'medium',
      created_at: '',
      company_name: r.company_name,
      position: r.position,
      status: r.status,
    }))
    return groupActions(items)
  }, [summary?.todo_items])

  const week = useMemo(() => (summary ? buildWeek(summary) : []), [summary])

  if (summaryQ.isLoading && !summary) return <PageSpinner />
  // refetch 失败时 react-query 仍保留 data，但 isError 为真。有缓存就继续
  // 画看板，只在顶部提示；整页换成错误卡会把好好的内容瞬间抹掉。
  if (summaryQ.isError && !summary) {
    return (
      <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Card padding="18px">
          <ErrorText>首页数据加载失败，请检查网络后重试。</ErrorText>
          <Button variant="primary" size="sm" onClick={() => summaryQ.refetch()} style={{ marginTop: 12 }}>
            重新加载
          </Button>
        </Card>
      </section>
    )
  }
  if (!summary) return <PageSpinner />

  const weekTotals = {
    submitted: summary.submitted_week,
    interviews: summary.interviews_week,
    replied: summary.replied_week,
    overdue: summary.todos.overdue,
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {summaryQ.isError && (
        <Card padding="12px 14px">
          <ErrorText>刷新失败，正在显示上次加载的内容。</ErrorText>
          <Button variant="ghost" size="sm" onClick={() => summaryQ.refetch()} style={{ marginTop: 8 }}>
            重试
          </Button>
        </Card>
      )}
      {toast && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span className="grow">
            <ErrorText>{toast}</ErrorText>
          </span>
          <Button variant="ghost" size="sm" onClick={() => setToast('')}>
            知道了
          </Button>
        </div>
      )}

      <WeekStrip week={week} totals={weekTotals} />

      <div className="metric-grid">
        <Kpi label="进行中" value={summary.in_progress} delta="applied…interviewing（不含归档）" />
        <Kpi label="本周投递" value={summary.submitted_week} delta="本周内发生的投递" />
        <Kpi label="待回复" value={summary.awaiting_reply} delta="已投递且尚无首次回复（不含归档）" />
        <Kpi
          label="逾期待办"
          value={summary.todos.overdue}
          delta={summary.todos.overdue > 0 ? '需要今天处理' : '没有逾期'}
          deltaColor={summary.todos.overdue > 0 ? 'var(--danger)' : 'var(--positive)'}
        />
      </div>

      {groups.length === 0 && (
        <EmptyHint>
          <p style={{ margin: 0, font: 'var(--type-body-sm)' }}>今天没有待办 🎉</p>
          <p style={{ margin: 0, fontSize: 13 }}>在岗位详情添加“下一步行动”或独立待办，就会出现在这里。</p>
          <Button variant="primary" size="sm" onClick={() => nav('/database')}>
            去添加岗位
          </Button>
        </EmptyHint>
      )}

      <div className="split-grid">
        {groups.length > 0 && (
          <Card style={PANEL}>
            <div className="panel-head">
              <PanelTitle>行动清单</PanelTitle>
              <span
                style={{
                  fontSize: 11,
                  border: '1px solid var(--border)',
                  padding: '1px 7px',
                  color: 'var(--neutral-700)',
                }}
              >
                {summary.todos.open} 项待处理
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>
                完成后自动推进对应岗位
              </span>
            </div>
            {groups.map((g) => (
              <div key={g.title}>
                <div className="panel-subhead">
                  <Dot color={g.dot} />
                  <span>{g.title}</span>
                  <span
                    style={{
                      fontSize: 11,
                      color: 'var(--text-muted)',
                      fontFamily: 'var(--font-body)',
                      fontWeight: 400,
                    }}
                  >
                    {g.items.length} 项
                  </span>
                </div>
                {g.items.map((t) => (
                  <TodoRow
                    key={t.id}
                    item={t}
                    // 只冻结**正在操作的那一行**：以前传的是
                    // `doneMut.isPending || postponeMut.isPending`，点一条
                    // 「完成」会把整张清单的按钮全灰掉，网络一慢就没法连着处理。
                    busy={busyTodoId === t.id}
                    onOpen={() => t.application_id && nav(`/apps/${t.application_id}`)}
                    onDone={() => {
                      setBusyTodoId(t.id)
                      doneMut.mutate({ actionId: t.action_id ?? null, applicationId: t.application_id ?? null })
                    }}
                    onPostpone={() => {
                      if (t.action_id == null) return
                      setBusyTodoId(t.id)
                      postponeMut.mutate(t.action_id)
                    }}
                  />
                ))}
              </div>
            ))}
          </Card>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <UpcomingInterviews
            upcoming={summary.upcoming}
            assessments={summary.upcoming_assessments ?? []}
            loading={summaryQ.isLoading}
            onOpen={(id) => nav(`/apps/${id}`)}
          />
          <ActivityFeed rows={summary.recent} onOpen={(id) => nav(`/apps/${id}`)} />
        </div>
      </div>

      <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{summary.scope_note}</p>
    </section>
  )
}

function Kpi({
  label,
  value,
  delta,
  deltaColor = 'var(--text-muted)',
}: {
  label: string
  value: number | string
  delta: string
  deltaColor?: string
}) {
  return (
    <div style={{ padding: '15px 16px' }}>
      <div className="micro">{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 40, lineHeight: 1 }}>
          {value}
        </span>
      </div>
      <div style={{ fontSize: 12, color: deltaColor, marginTop: 6 }}>{delta}</div>
    </div>
  )
}

function WeekStrip({
  week,
  totals,
}: {
  week: ReturnType<typeof buildWeek>
  totals: { submitted: number; interviews: number; replied: number; overdue: number }
}) {
  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <h4 style={{ font: 'var(--type-h4)', margin: 0 }}>本周工序</h4>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>
          投递 <Num color="var(--text)">{totals.submitted}</Num> · 面试 <Num color="var(--text)">{totals.interviews}</Num> ·
          回复 <Num color="var(--text)">{totals.replied}</Num> · 逾期{' '}
          <Num color={totals.overdue > 0 ? 'var(--danger)' : 'var(--text)'}>{totals.overdue}</Num>
        </span>
      </div>
      <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
        <div className="mesh" style={{ gridTemplateColumns: 'repeat(7,minmax(104px,1fr))', minWidth: 756 }}>
          {week.map((d) => (
            <div
              key={d.weekday}
              style={{
                minHeight: 132,
                padding: '9px 9px 11px',
                background: d.isToday ? 'var(--accent-100)' : 'var(--bg)',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11, letterSpacing: '.1em', color: 'var(--neutral-600)' }}>{d.weekday}</span>
                <span
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 600,
                    fontSize: 17,
                    color: d.isToday ? 'var(--accent-700)' : 'var(--text)',
                  }}
                >
                  {d.dayNum}
                </span>
              </div>
              {d.items.slice(0, 3).map((e, i) => (
                <div
                  key={i}
                  style={{
                    padding: '4px 6px',
                    background: CHIP_TONES[e.tone].bg,
                    color: CHIP_TONES[e.tone].fg,
                    borderLeft: '2px solid ' + CHIP_TONES[e.tone].bar,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      fontSize: 10,
                      letterSpacing: '.08em',
                      color: 'var(--neutral-600)',
                      lineHeight: 1.3,
                    }}
                  >
                    {e.kind}
                  </span>
                  <span
                    className="ellipsis"
                    style={{ display: 'block', fontSize: 12, fontWeight: 500, lineHeight: 1.3 }}
                  >
                    {e.who}
                  </span>
                </div>
              ))}
              {d.items.length > 3 && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>+{d.items.length - 3}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function TodoRow({
  item,
  busy,
  onOpen,
  onDone,
  onPostpone,
}: {
  item: TodoItem
  busy: boolean
  onOpen: () => void
  onDone: () => void
  onPostpone: () => void
}) {
  const due = item.due_ts ?? item.due_date
  const dueDay = item.due_date || null
  // “today” is taken in the user's configured zone; an instant due is compared
  // by its user-zone calendar day (never browser-local midnight), and a
  // date-only due by day-key — both agree with the server's overdue window.
  const todayStr = toDayString(new Date().toISOString(), effectiveZone()) ?? ''
  const overdue =
    item.due_ts != null
      ? (toDayString(item.due_ts, effectiveZone()) ?? '') < todayStr
      : dueDay != null && dueDay < todayStr
  const derived = item.action_id == null
  return (
    <div className="panel-row">
      <span className="grow" onClick={onOpen} style={{ cursor: 'pointer' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="ellipsis" style={{ fontSize: 14, fontWeight: 500 }}>
            {item.company_name ?? '未关联岗位'}
          </span>
          {item.position && (
            <span className="ellipsis" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {item.position}
            </span>
          )}
          {item.status && <StatusChip status={item.status} />}
        </span>
        <span style={{ display: 'block', fontSize: 13, marginTop: 2 }}>{item.title}</span>
      </span>
      {/* 必须显式传时区：due 可能是一个瞬间（due_ts），fmtDay 不给 zone 就按
          浏览器时区算日历日——而上面那行 overdue 用的是用户配置时区，两者会在
          「浏览器与账号时区不同」的人身上给出差一天的日期和红字。 */}
      <Num color={overdue ? 'var(--danger)' : 'var(--text-muted)'}>
        {overdue ? `逾期 ${fmtDay(due, effectiveZone())}` : fmtDay(due, effectiveZone())}
      </Num>
      {overdue && !derived && (
        <Button variant="ghost" size="sm" disabled={busy} onClick={onPostpone} title="延期一天">
          延期
        </Button>
      )}
      <Button variant="secondary" size="sm" disabled={busy} onClick={onDone}>
        完成
      </Button>
    </div>
  )
}

/** OA / 作业轮次的短名词：与日历 / 提醒的措辞一致。 */
function oaNoun(kind: string): string {
  if (kind === 'take_home') return '作业'
  if (kind === 'other') return '测评'
  return 'OA'
}

function UpcomingInterviews({
  upcoming,
  assessments,
  loading,
  onOpen,
}: {
  upcoming: HomeSummary['upcoming']
  assessments: HomeSummary['upcoming_assessments']
  loading: boolean
  onOpen: (id: number) => void
}) {
  if (loading) return null
  // Interviews and OA rounds share one chronological panel (方案 §5: 首页接入
  // OA 计划 / 截止时间): both are "接下来要做的事", and merging keeps the feed
  // honest about what comes next instead of hiding OAs below the fold.
  const rows = [
    ...upcoming.map((i) => ({
      key: `i-${i.id}`,
      at: i.scheduled_at,
      applicationId: i.application_id,
      head: `${i.company_name} · ${i.round_name || '面试'}`,
      detail: `${fmtDateTime(i.scheduled_at)} · ${formatLabel(i.format) || '待定'}${i.location ? ` · ${i.location}` : ''}`,
    })),
    ...assessments.map((a) => ({
      key: `a-${a.id}`,
      at: a.planned_at,
      applicationId: a.application_id,
      head: `${a.company_name} · ${oaNoun(a.kind)} ${a.name || ''}`.trimEnd(),
      detail:
        `${fmtDateTime(a.planned_at)} · ${oaNoun(a.kind)}做起来` +
        (a.due_at ? ` · 截止 ${fmtDateTime(a.due_at)}` : '') +
        (a.progress === 'completed' ? ' · 已完成' : ''),
    })),
  ].sort((x, y) => x.at.localeCompare(y.at))
  return (
    <Card style={PANEL}>
      <div className="panel-head">
        <PanelTitle>即将到来的面试 / OA</PanelTitle>
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: '14px 16px', fontSize: 13, color: 'var(--text-muted)' }}>
          还没有排期的面试或 OA。在岗位详情里安排面试 / 记录一轮测评后会显示在这里。
        </div>
      ) : (
        rows.map((e) => {
          // The date badge must show the USER-zone calendar day: the home feed
          // and calendar column bucket events in the user's configured zone, so
          // rendering the day with the browser's local getters would disagree
          // when the browser zone differs (e.g. a Dublin browser + Shanghai
          // user — an interview that is tomorrow in the user's zone would show
          // the wrong day on the badge). Derive it from the zone day key.
          const dayKey = toDayString(e.at, effectiveZone()) ?? ''
          const dayNum = dayKey.slice(8, 10) || ''
          const monthNum = dayKey.slice(5, 7) || ''
          return (
            <button key={e.key} className="panel-row" onClick={() => onOpen(e.applicationId)}>
              <span
                style={{
                  width: 42,
                  textAlign: 'center',
                  padding: '3px 0',
                  border: '1px solid var(--border)',
                  flex: '0 0 auto',
                }}
              >
                <span
                  style={{
                    display: 'block',
                    fontFamily: 'var(--font-display)',
                    fontWeight: 600,
                    fontSize: 17,
                    lineHeight: 1.1,
                  }}
                >
                  {String(Number(dayNum)).padStart(2, '0')}
                </span>
                <span style={{ display: 'block', fontSize: 10, color: 'var(--text-muted)' }}>{Number(monthNum)} 月</span>
              </span>
              <span className="grow">
                <span className="ellipsis" style={{ display: 'block', fontSize: 14, fontWeight: 500 }}>
                  {e.head}
                </span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  {e.detail}
                </span>
              </span>
            </button>
          )
        })
      )}
    </Card>
  )
}

function ActivityFeed({ rows, onOpen }: { rows: HomeSummary['recent']; onOpen: (id: number) => void }) {
  return (
    <Card style={PANEL}>
      <div className="panel-head">
        <PanelTitle>最近动态</PanelTitle>
      </div>
      <div style={{ padding: '12px 16px 14px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {rows.length === 0 && <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>还没有记录</span>}
        {rows.map((r) => (
          <button
            key={r.id}
            className="menu-item"
            style={{ alignItems: 'flex-start', padding: '5px 0' }}
            onClick={() => onOpen(r.id)}
          >
            <span style={{ marginTop: 5 }}>
              <Dot color={statusMeta(r.status).dot} />
            </span>
            <span className="grow ellipsis" style={{ fontSize: 13, lineHeight: 1.45, textAlign: 'left' }}>
              {r.company_name} · {statusMeta(r.status).label}
              {r.next_action ? ` — ${r.next_action}` : ''}
            </span>
            <Num color="var(--text-muted)">{fmtDate(r.updated_at)}</Num>
          </button>
        ))}
      </div>
    </Card>
  )
}
